/* eslint-disable quote-props */
import { Box, SelectChangeEvent } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { usePrescriptions } from "../../../../services/prescriptions/prescriptions.service";
import { Loading } from "../../../../components";
import NoPrescriptions from "./components/NoPrescriptions/NoPrescriptions";
import PrescriptionsHeader from "./components/PrescriptionsHeader/PrescriptionsHeader";
import PrescriptionAccordion from "./components/PrescriptionAccordion/PrescriptionAccordion";
import PrescriptionCard from "./components/PrescriptionCard/PrescriptionCard";
import { EPrescriptionStatus } from "../../../../services/prescriptions/prescriptions.interface";
import NoContent from "../components/NoContent/NoContent";
import PrescriptionsLimitMessage from "./components/PrescriptionsLimitMessage/PrescriptionsLimitMessage";
import { userActivityLogger } from "../../../../utils/mixpanel";
import { MIXPANEL_EVENTS } from "../../../../config/mixPanelEvents";
import TryAgain from "./components/TryAgain/TryAgain";

interface PrescriptionsContentProps {
  shopUrl: string;
}

const PrescriptionsContent = ({ shopUrl }: PrescriptionsContentProps) => {
  const [petId, setPetId] = useState("all");

  const { executeGetPrescriptions, data, loading } = usePrescriptions();

  useEffect(() => {
    executeGetPrescriptions(petId);
  }, [executeGetPrescriptions, petId]);

  const { prescriptions, hasMore, success } = data;

  const { actionsRequired, activePrescriptions } = useMemo(() => {
    const actionsRequired = prescriptions
      .filter((p) => p.status === EPrescriptionStatus.EXPIRED || p.status === EPrescriptionStatus.EXPIRING_SOON)
      .sort((a, b) => {
        if (a.status === EPrescriptionStatus.EXPIRED && b.status !== EPrescriptionStatus.EXPIRED) return -1;
        if (a.status !== EPrescriptionStatus.EXPIRED && b.status === EPrescriptionStatus.EXPIRED) return 1;
        return 0;
      });
    const activePrescriptions = prescriptions.filter(
      (p) => p.status !== EPrescriptionStatus.EXPIRED && p.status !== EPrescriptionStatus.EXPIRING_SOON,
    );
    return { actionsRequired, activePrescriptions };
  }, [prescriptions]);

  useEffect(() => {
    if (!loading && prescriptions.length > 0) {
      userActivityLogger(MIXPANEL_EVENTS.PHARMACY_PRESCRIPTIONS_LOADED, {
        total_count: prescriptions.length,
        renewal_count: prescriptions.filter((p) => p.status === EPrescriptionStatus.EXPIRED).length,
        source: "shop",
      });
    }
  }, [loading, prescriptions, petId]);

  const handlePetChange = useCallback((event: SelectChangeEvent<string>) => {
    setPetId(event.target.value);
  }, []);

  const handleRetry = useCallback(() => {
    executeGetPrescriptions(petId);
  }, [executeGetPrescriptions, petId]);

  if (loading) {
    return (
      <Box className="w-full flex justify-center items-center min-h-[400px]">
        <Loading />
      </Box>
    );
  }
  if (!success) {
    return <TryAgain onTryAgain={handleRetry} />;
  }

  return (
    <Box className="flex flex-col gap-6">
      {prescriptions.length > 0 ? (
        <Box className="flex flex-col gap-4">
          <PrescriptionsHeader petId={petId} prescriptionsUrl={shopUrl} onPetChange={handlePetChange} />
          <Box
            className="flex gap-4"
            sx={{ flexDirection: actionsRequired.length === 0 ? "column-reverse" : "column" }}
          >
            <PrescriptionAccordion title="Actions Required" count={actionsRequired.length} variant="actionRequired">
              {actionsRequired.length > 0 ? (
                actionsRequired.map((prescription) => (
                  <PrescriptionCard key={prescription.id} prescription={prescription} shopUrl={shopUrl} />
                ))
              ) : (
                <NoContent
                  size="small"
                  imageSrc="/assets/images/no-prescription-actions.svg"
                  imageAlt="No prescription actions illustration"
                  title="There are no actions to perform"
                  description="It seems all of your prescriptions are active."
                />
              )}
            </PrescriptionAccordion>
            <PrescriptionAccordion
              title="Active Prescriptions"
              count={activePrescriptions.length}
              variant="activePrescriptions"
            >
              {activePrescriptions.length > 0 ? (
                activePrescriptions.map((prescription) => (
                  <PrescriptionCard key={prescription.id} prescription={prescription} shopUrl={shopUrl} />
                ))
              ) : (
                <NoContent
                  size="small"
                  imageSrc="/assets/images/no-active-prescriptions.svg"
                  imageAlt="No active prescriptions illustration"
                  title="You don’t have any active presctiptions"
                  description={
                    <>
                      There are no active prescriptions to display, <br />
                      which must mean all of your pets are healthy. <br />
                      Go and have fun with them.
                    </>
                  }
                />
              )}
            </PrescriptionAccordion>
          </Box>
          {hasMore && <PrescriptionsLimitMessage />}
        </Box>
      ) : (
        <NoPrescriptions shopUrl={shopUrl} />
      )}
    </Box>
  );
};

export default PrescriptionsContent;
